import { useState } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { AuthField } from '@/components/Form/Auth/AuthField';
import { AuthFieldContainer } from '@/components/Form/Auth/AuthFieldContainer';
import { FormManagementProps } from '@/type/form.types';
import { Input } from '@/components/Controls/Input';
import { twMerge } from 'tailwind-merge';
import { AuthButton } from '@/components/Custom/Button/AuthButton';
import { AuthLinkButton } from '@/components/Custom/Button/AuthLinkButton';
import { Select } from '@/components/Controls/Select';
import { useGetExistingUser } from '@/hooks/user.hooks';
import { allFieldErrorsEmpty } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';

export const RegisterManagement = <TSchema extends FieldValues>({
  t,
  watch,
  register,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const emailValue = watch && watch('email' as Path<TSchema>);

  const { data: existingUser } = useGetExistingUser({
    email: emailValue,
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const userErrorMessages = existingUser?.data?.errorMessages;

    if (userErrorMessages?.includes(FieldError.EMAIL_EXISTS)) {
      setError('email' as Path<TSchema>, {
        message: t('common:userEmailAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-5">
        <AuthFieldContainer>
          <AuthField title={t('common:title')} required>
            <Select
              {...(register && register('title' as Path<TSchema>))}
              errorMessage={errors && (errors.title?.message as string)}
              placeholder={t('common:selectTitle')}
              options={[
                {
                  label: 'M.',
                  value: 'mister',
                },
                {
                  label: 'Mme',
                  value: 'miss',
                },
              ]}
            />
          </AuthField>
        </AuthFieldContainer>
        <AuthFieldContainer>
          <AuthField title={t('common:lastname')} required>
            <Input
              {...register('lastname' as Path<TSchema>)}
              errorMessage={errors && (errors.lastname?.message as string)}
              placeholder={t('common:lastname')}
              type="search"
              inputClassName="h-10"
            />
          </AuthField>
        </AuthFieldContainer>
        <AuthFieldContainer>
          <AuthField title={t('common:firstname')} required>
            <Input
              {...register('firstname' as Path<TSchema>)}
              errorMessage={errors && (errors.firstname?.message as string)}
              placeholder={t('common:firstname')}
              type="search"
              inputClassName="h-10"
            />
          </AuthField>
        </AuthFieldContainer>
        <AuthFieldContainer>
          <AuthField title={t('common:phoneNumber')} required>
            <Input
              {...register('phone' as Path<TSchema>)}
              errorMessage={errors && (errors.phone?.message as string)}
              placeholder={t('common:phoneNumber')}
              type="tel"
              inputClassName="h-10"
            />
          </AuthField>
        </AuthFieldContainer>
        <AuthFieldContainer>
          <AuthField title={t('common:email')} required>
            <Input
              {...register('email' as Path<TSchema>)}
              errorMessage={errors && (errors.email?.message as string)}
              placeholder={t('common:email')}
              type="email"
              inputClassName="h-10"
            />
          </AuthField>
        </AuthFieldContainer>
        <AuthFieldContainer>
          <AuthField title={t('common:password')} required>
            <Input
              {...register('password' as Path<TSchema>)}
              errorMessage={errors && (errors.password?.message as string)}
              placeholder={t('common:password')}
              type={showPassword ? 'text' : 'password'}
              rightIcon={
                <i
                  className={twMerge(
                    'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                    showPassword ? 'ki-eye-slash' : 'ki-eye'
                  )}
                  onClick={() => setShowPassword((prevState) => !prevState)}
                />
              }
              inputClassName="h-10"
              autoComplete="new-password"
            />
          </AuthField>
        </AuthFieldContainer>
        <AuthFieldContainer>
          <AuthField title={t('common:reEnterPassword')} required>
            <Input
              {...register('passwordConfirm' as Path<TSchema>)}
              errorMessage={
                errors && (errors.passwordConfirm?.message as string)
              }
              placeholder={t('common:reEnterPassword')}
              type={showPasswordConfirm ? 'text' : 'password'}
              rightIcon={
                <i
                  className={twMerge(
                    'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                    showPasswordConfirm ? 'ki-eye-slash' : 'ki-eye'
                  )}
                  onClick={() =>
                    setShowPasswordConfirm((prevState) => !prevState)
                  }
                />
              }
              inputClassName="h-10"
              autoComplete="new-password"
            />
          </AuthField>
        </AuthFieldContainer>
      </div>
      <div className="mt-7">
        <AuthButton
          text={
            submitDisabled ? `${t('common:loading')}...` : t('common:register')
          }
          disabled={submitDisabled}
        />
      </div>
      <div className="mt-5 text-gray-600 text-md text-center">
        {`${t('alreadyHaveAnAccount')} ?`}
      </div>
      <div className="mt-5">
        <AuthLinkButton linkTo="/login" text={t('logIn')} />
      </div>
    </form>
  );
};
